import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { deleteUserMutation } from '@/mutations/user.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { useFormik } from 'formik';
import { analytics } from '@/lib/analytics';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

export const DeleteAccountDialog: FunctionComponent<{ userId: string }> = ({ userId }) => {
  const { dismissActiveDialog } = useDialog();
  const navigate = useNavigate();
  const { t } = useTranslation('account');
  const formik = useFormik({
    initialValues: {
      password: '',
      confirmText: '',
    },
    validate: (values) => {
      const errors: any = {};
      if (values.confirmText !== 'DELETE') {
        errors.confirmText = t('deleteAccountDialog.pleaseTypeDelete');
      }
      return errors;
    },
    onSubmit: async (_values) => {
      const toastId = toast.loading(t('deleteAccountDialog.deletingAccount'));
      try {
        await execute(deleteUserMutation, { id: userId, password: _values.password });
        analytics.capture('Account Deleted');
        toast.success(t('deleteAccountDialog.accountDeleted'), { id: toastId });
      } catch (error) {
        toast.error(t('deleteAccountDialog.failedDeleteAccount'), { id: toastId });
      }
      dismissActiveDialog();
      navigate('/auth/logout', { replace: true });
    },
  });

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-0.5'>
        <h3 className='font-semibold text-rose-600'>{t('deleteAccountDialog.title')}</h3>
        <p className='text-sm leading-none text-slate-700'>
          {t('deleteAccountDialog.description')}
        </p>
      </div>
      <form onSubmit={formik.handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='password'>{t('deleteAccountDialog.currentPassword')}</Label>
          <Input
            id='password'
            name='password'
            type='password'
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t('deleteAccountDialog.currentPasswordPlaceholder')}
          />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='confirmText'>{t('deleteAccountDialog.typeDeleteToConfirm')}</Label>
          <Input
            id='confirmText'
            name='confirmText'
            value={formik.values.confirmText}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t('deleteAccountDialog.typeDelete')}
          />
          {formik.errors.confirmText && <p className='text-sm text-red-600'>{formik.errors.confirmText}</p>}
        </div>
        <div className='flex justify-end space-x-2'>
          <Button type='button' variant='outline' onClick={dismissActiveDialog}>
            {t('deleteAccountDialog.cancel')}
          </Button>
          <Button type='submit' variant='destructive' disabled={formik.isSubmitting}>
            {t('deleteAccountDialog.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
};
